import React from 'react';
import { Camera, Video, Zap, Hash, Clock, Smile, Star, Share2, Images, Wand2 } from 'lucide-react';
import type { AddOn } from '../types';
import { foto } from '../imagenes';

interface ServicesGridProps {
  services: AddOn[];
}

/** Un icono distinto por complemento, para que la retícula no se lea plana. */
const iconFor = (id: string) => {
  switch (id) {
    case 'photobooth': return <Camera size={32} />;
    case 'videobooth': return <Video size={32} />;
    case 'boomerang': return <Zap size={32} />;
    case 'hashtag': return <Hash size={32} />;
    case 'slowmo': return <Clock size={32} />;
    case 'encuestas': return <Star size={32} />;
    case 'chroma': return <Wand2 size={32} />;
    case 'adicionales': return <Images size={32} />;
    default: return <Smile size={32} />;
  }
};

const ServicesGrid: React.FC<ServicesGridProps> = ({ services }) => (
  <section className="bg-[#F9F6F2] py-24">
    <div className="container mx-auto px-6">
      <div className="mb-16 flex flex-col items-end justify-between md:flex-row">
        <div className="max-w-xl">
          <h2 className="mb-4 font-serif text-4xl font-bold text-black md:text-6xl">
            Más que fotos,
            <br />
            <span className="italic text-cabina-red">Pura Diversión</span>
          </h2>
          <p className="text-lg text-gray-600">
            Personaliza tu experiencia con nuestros complementos. Haz que cada segundo cuente y
            sea compartible.
          </p>
        </div>
        <div className="hidden md:block" aria-hidden="true">
          <div className="animate-float">
            <Share2 size={48} className="text-cabina-red opacity-20" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {services.map((service, index) => (
          <article
            key={service.id}
            className={`group relative overflow-hidden rounded-lg shadow-sm ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${
              index === 0 || index === 7 ? 'col-span-2 aspect-[2/1]' : 'col-span-1 aspect-square'
            }`}
          >
            <img
              {...foto(service.image, '(min-width: 768px) 25vw, 50vw')}
              alt={`${service.name} — ${service.description}`}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10 transition-colors duration-500 group-hover:from-cabina-wine/90" />

            <div
              className="absolute -bottom-4 -right-4 rotate-12 scale-150 text-white/10 transition-colors group-hover:text-white/20"
              aria-hidden="true"
            >
              {iconFor(service.id)}
            </div>

            <div className="absolute inset-0 z-10 flex flex-col justify-between p-4 md:p-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 md:h-12 md:w-12 text-white shadow-sm backdrop-blur-md transition-colors group-hover:bg-white group-hover:text-cabina-red">
                {iconFor(service.id)}
              </span>

              <div>
                <h3 className="mb-1.5 font-serif text-base font-bold leading-tight text-white md:mb-2 md:text-xl">
                  {service.name}
                </h3>
                <p className="line-clamp-2 text-[11px] leading-snug text-white/80 md:text-sm">
                  {service.description}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default ServicesGrid;
